import { useEffect, useState } from "react";
import {
  getDrivers,
  getDriver,
  createDriver,
  updateDriver,
  deleteDriver,
  approveDriver,
  disapproveDriver,
} from "../services/driver.service";

import DriverTable from "../components/driver/DriverTable";
import DriverDetailsModal from "../components/driver/DriverDetailsModal";
import DriverCreateModal from "../components/driver/DriverCreateModal";
import DriverEditModal from "../components/driver/DriverEditModal";
import DriverDeleteModal from "../components/driver/DriverDeleteModal";
import DriverHeader from "../components/driver/DriverHeader";
import AssignOrderTypeModal from "../components/driver/AssignOrderTypeModal";
import { useToast } from "../context/ToastContext";

export default function Drivers() {
  const { showToast } = useToast();
  const [data, setData] = useState({ data: [], last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [approveFilter, setApproveFilter] = useState("all");
  const [availFilter, setAvailFilter] = useState("all");

  const [selected, setSelected] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [orderTypeDriver, setOrderTypeDriver] = useState(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadDrivers();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [page, search, approveFilter, availFilter]);

  async function loadDrivers() {
    setLoading(true);
    try {
      const params = {
        page,
        search,
        approve: approveFilter,
        avail: availFilter,
      };
      const res = await getDrivers(params);
      setData(res);
    } catch (err) {
      showToast("Erreur de chargement", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleApprove(driver) {
    try {
      if (driver.isApproved) {
        await disapproveDriver(driver.id);
        showToast("Chauffeur désapprouvé", "success");
      } else {
        await approveDriver(driver.id, true);
        showToast("Chauffeur approuvé", "success");
      }
      loadDrivers();
    } catch (err) {
      showToast("Erreur lors de l'approbation", "error");
    }
  }

  async function handleView(driver) {
    try {
      const fullDriver = await getDriver(driver.id);
      setSelected(fullDriver);
    } catch (err) {
      showToast("Impossible de charger les détails", "error");
    }
  }

  async function handleCreate(payload) {
    try {
      await createDriver(payload);
      showToast("Chauffeur créé avec succès", "success");
      setOpenCreate(false);
      setPage(1);
      loadDrivers();
    } catch (err) {
      showToast("Erreur lors de la création", "error");
    }
  }

  async function handleEditSubmit(id, payload) {
    try {
      await updateDriver(id, payload);
      showToast("Chauffeur modifié", "success");
      setEditTarget(null);
      loadDrivers();
    } catch (err) {
      showToast("Erreur lors de la modification", "error");
    }
  }

  async function handleDelete(id) {
    try {
      await deleteDriver(id);
      showToast("Chauffeur supprimé", "success");
      setDeleteTarget(null);
      loadDrivers();
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  }

  if (loading && data.data.length === 0) {
    return <div className="loaderContainer">Chargement des chauffeurs...</div>;
  }

  return (
    <div className="page">
      <DriverHeader
        total={data.total}
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(1);
        }}
        approveFilter={approveFilter}
        onApproveChange={(v) => {
          setApproveFilter(v);
          setPage(1);
        }}
        availFilter={availFilter}
        onAvailChange={(v) => {
          setAvailFilter(v);
          setPage(1);
        }}
        onAdd={() => setOpenCreate(true)}
      />

      <div className={loading ? "tableContainer loading" : "tableContainer"}>
        <DriverTable
          drivers={data.data}
          onView={handleView}
          onEdit={setEditTarget}
          onDelete={setDeleteTarget}
          onToggleApprove={handleToggleApprove}
          onAssignOrderType={(driver) => setOrderTypeDriver(driver)}
        />
      </div>

      <div className="paginationContainer">
        <div className="pagiInfo">
          Page <strong>{page}</strong> sur {data.last_page}
        </div>
        <div className="pagiControls">
          <button
            className="pagiBtn"
            disabled={page === 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Précédent
          </button>
          <button
            className="pagiBtn"
            disabled={page === data.last_page || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant
          </button>
        </div>
      </div>


      <DriverCreateModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSubmit={handleCreate}
      />

      <DriverEditModal
        open={!!editTarget}
        driver={editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEditSubmit}
      />

      <DriverDetailsModal driver={selected} onClose={() => setSelected(null)} />

      <AssignOrderTypeModal
        driver={orderTypeDriver}
        onClose={() => setOrderTypeDriver(null)}
      />

      <DriverDeleteModal
        driver={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
